import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { KeyboardArrowDown, Search } from "@mui/icons-material";
import UserTable from "../../components/user/UserTable";
import { useNotification } from "../../../../../utils/NotificationsContext";
import DeleteConfirmation from "../../../../../views/components/common/pop-up/DeleteConfirmation";
import { SortType } from "../../../../../models/SortType";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  UserActiveStatus,
  UserFilters,
} from "../../../../../models/User/UserFilters";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { Customer } from "../../../../../models/Customer/Customer";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

const customerLabel = (customer: { id?: number; name?: string }) =>
  customer.name || `Customer #${customer.id}`;

const UserListing: React.FC = () => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const { userStore, customerStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [activateId, setActivateId] = useState<number | null>(null);

  const initialCustomerId = searchParams.get("customer_id")
    ? parseInt(searchParams.get("customer_id") as string, 10)
    : undefined;

  const activeParam = searchParams.get("active");
  const initialActiveStatus: UserActiveStatus =
    activeParam === "all"
      ? "all"
      : activeParam === "false"
        ? "inactive"
        : "active";

  const initialParams: UserFilters = {
    page_no: searchParams.get("page_no")
      ? parseInt(searchParams.get("page_no") as string, 10)
      : searchParams.get("page")
        ? parseInt(searchParams.get("page") as string, 10)
        : 1,
    search_text: searchParams.get("search_text") || undefined,
    active_status: initialActiveStatus,
    customer_id: initialCustomerId,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
  };

  const [params, setParams] = useState<UserFilters>(initialParams);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || ""
  );
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [isCustomerSearching, setIsCustomerSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);


  const debouncedSearch = useDebounce(searchTerm, 400);
  const debouncedCustomerSearch = useDebounce(customerSearchText, 350);

  const hasUserSectionAccess =
    userStore.hasPermission(EmployeeRoles.VIEW_USER) ||
    userStore.hasPermission(EmployeeRoles.EDIT_USER) ||
    userStore.hasPermission(EmployeeRoles.CREATE_USER);

  // If initial customer ID is in URL, fetch details to set as selected
  useEffect(() => {
    if (initialCustomerId) {
      customerStore.customerAPI
        .getCustomerDetailsById(initialCustomerId)
        .then((res: any) => {
          if (res?.id) {
            const customer = { id: res.id, name: res.name };
            setSelectedCustomer(customer);
            setCustomerSearchText(customerLabel(customer));
          }
        })
        .catch(() => {
          const customer = { id: initialCustomerId };
          setSelectedCustomer(customer);
          setCustomerSearchText(customerLabel(customer));
        });
    }
  }, [initialCustomerId, customerStore]);

  // Debounced search for customers (only when user types 2+ characters)
  useEffect(() => {
    const trimmed = debouncedCustomerSearch.trim();
    const selectedLabel = selectedCustomer ? customerLabel(selectedCustomer) : "";
    if (selectedLabel && trimmed === selectedLabel) return;
    if (trimmed.length >= 2) {
      setIsCustomerSearching(true);
      customerStore
        .searchCustomers({
          page_no: 1,
          search_text: trimmed,
          limit: 20,
        })
        .catch(() => { })
        .finally(() => {
          setIsCustomerSearching(false);
        });
    }
  }, [debouncedCustomerSearch, customerStore, selectedCustomer]);

  const customerOptions = useMemo(() => {
    const list = [...customerStore.customers];
    if (
      selectedCustomer &&
      !list.some((c) => c.id === selectedCustomer.id)
    ) {
      list.unshift(selectedCustomer as any);
    }
    return list;
  }, [customerStore.customers, selectedCustomer]);

  // Sync debounced search (user name or email) to params
  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    setParams((prev) => {
      const current = prev.search_text || "";
      if (current === trimmed) return prev;
      return {
        ...prev,
        search_text: trimmed.length > 0 ? trimmed : undefined,
        page_no: 1,
      };
    });
  }, [debouncedSearch]);

  // Fetch users on params change
  useEffect(() => {
    if (!hasUserSectionAccess) return;

    setIsLoading(true);
    userStore
      .getUsers(params)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
          "Something went wrong! Please try again later."
        )
      )
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Sync URL search params
  useEffect(() => {
    const nextSearchParams = new URLSearchParams();
    if (params.page_no && params.page_no > 1) {
      nextSearchParams.set("page_no", params.page_no.toString());
    }
    if (params.limit && params.limit !== 30) {
      nextSearchParams.set("limit", params.limit.toString());
    }
    if (params.search_text) {
      nextSearchParams.set("search_text", params.search_text);
    }
    if (params.customer_id) {
      nextSearchParams.set("customer_id", params.customer_id.toString());
    }
    if (params.active_status === "all") {
      nextSearchParams.set("active", "all");
    } else if (params.active_status === "inactive") {
      nextSearchParams.set("active", "false");
    } else if (params.active_status === "active") {
      nextSearchParams.set("active", "true");
    }
    if (params.sort_by && params.sort_by !== "created_date") {
      nextSearchParams.set("sort_by", params.sort_by);
    }
    if (params.sort_type && params.sort_type !== SortType.DESC) {
      nextSearchParams.set("sort_type", params.sort_type);
    }

    const queryString = nextSearchParams.toString();
    navigate(queryString ? `?${queryString}` : location.pathname, {
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const trimmed = searchTerm.trim();
      setParams((prev) => ({
        ...prev,
        search_text: trimmed.length > 0 ? trimmed : undefined,
        page_no: 1,
      }));
    }
  };

  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    setCustomerSearchText(customer ? customerLabel(customer) : "");
    setParams((prev) => ({
      ...prev,
      customer_id: customer?.id ? customer.id : undefined,
      page_no: 1,
    }));
  };

  const handleActiveStatusChange = (val: UserActiveStatus) => {
    setParams((prev) => ({
      ...prev,
      active_status: val,
      page_no: 1,
    }));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const handleDeleteConfirm = async (userId: number) => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const res = await userStore.deleteUser(userId);
      setDeleteId(null);

      if (res?.success) {
        showSuccess(res?.result || "User deleted successfully");
        await userStore.getUsers(params);
        return;
      }

      showError("Error Occurred", "Error deleting user");
    } catch (err: any) {
      setDeleteId(null);
      showError(
        "Error",
        err?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateConfirm = async (userId: number) => {
    if (!userId) return;

    try {
      setIsLoading(true);
      const res = await userStore.activateUser(userId);
      setActivateId(null);

      if (res?.success) {
        showSuccess(res?.result?.message || res?.message || "User activated successfully");
        await userStore.getUsers(params);
        return;
      }

      showError("Error Occurred", "Error activating user");
    } catch (err: any) {
      setActivateId(null);
      showError(
        "Error",
        err?.response?.data?.message ||
        "Something went wrong! Please try again later."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const inputFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      height: 40,
      backgroundColor: colors.surface,
      fontSize: "0.875rem",
      "& fieldset": {
        borderColor: colors.border,
      },
      "&:hover fieldset": {
        borderColor: colors.accentPurple,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.accentPurple,
        borderWidth: 1.5,
      },
    },
  };

  return (
    <Grid2
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      sx={{
        px: { xs: 2, md: 3 },
        py: 3,
        backgroundColor: colors.pageBg,
        minHeight: "100vh",
      }}
    >
      {!hasUserSectionAccess ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.surface,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.4,
              mb: 2,
            }}
          >
            <CustomTypography variant="heading3">Users</CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors?.greyTextColor}
            >
              View and manage users. Search by name, email, or customer.
            </CustomTypography>
          </Box>
          <PermissionBlockLabel />
        </Paper>
      ) : (
        <Box sx={{ maxWidth: 1600, mx: "auto" }}>
          {/* Header Bar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <CustomTypography variant="heading3" color={colors.title}>
                Users
              </CustomTypography>
              <CustomTypography
                variant="body2"
                color={colors.subtitle}
                sx={{ mt: 0.5 }}
              >
                View and manage users. Search by name, email, or customer.
              </CustomTypography>
            </Box>

            {userStore.hasPermission(EmployeeRoles.CREATE_USER) && (
              <AppButton
                label="Create User"
                variant="primary"
                onClick={() =>
                  navigate("/users/add-edit-user", {
                    state: { listingSearch: location.search },
                  })
                }
              />
            )}
          </Box>

          {/* Table Container Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.surface,
              overflow: "hidden",
              boxShadow: colors.shadowSoft,
            }}
          >
            {/* Filter Bar */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                p: 2.5,
                gap: 2,
              }}
            >
              {/* Search Field for User Name or Email */}
              <TextField
                size="small"
                placeholder="Search for user name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: colors.subtitle, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 280, md: 340 },
                  ...inputFieldSx,
                }}
              />

              {/* Right Filters: Customer (Searchable with debounce) & Status */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                {/* Customer Searchable Autocomplete Dropdown */}
                <Autocomplete
                  size="small"
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    handleCustomerSelect(newValue);
                  }}
                  inputValue={customerSearchText}
                  onInputChange={(_, newInputValue, reason) => {
                    if (reason === "input" || reason === "reset") {
                      setCustomerSearchText(newInputValue);
                    } else if (reason === "clear") {
                      setCustomerSearchText("");
                      handleCustomerSelect(null);
                    }
                  }}
                  options={customerOptions}
                  getOptionLabel={(option) => customerLabel(option)}
                  isOptionEqualToValue={(option, val) => option.id === val.id}
                  loading={isCustomerSearching}
                  noOptionsText={
                    customerSearchText.trim().length < 2
                      ? ""
                      : "No customers found"
                  }
                  popupIcon={<KeyboardArrowDown sx={{ color: colors.subtitle }} />}
                  renderInput={(inputParams) => (
                    <TextField
                      {...inputParams}
                      placeholder="Customer"
                      InputProps={{
                        ...inputParams.InputProps,
                        endAdornment: (
                          <>
                            {isCustomerSearching ? (
                              <CircularProgress color="inherit" size={16} />
                            ) : null}
                            {inputParams.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{
                        width: { xs: "100%", sm: 200, md: 220 },
                        ...inputFieldSx,
                        "& .MuiOutlinedInput-root": {
                          ...inputFieldSx["& .MuiOutlinedInput-root"],
                          color: colors.label,
                          fontWeight: 500,
                        },
                      }}
                    />
                  )}
                />

                {/* Status Dropdown */}
                <Select
                  size="small"
                  value={params.active_status ?? "active"}
                  onChange={(e) =>
                    handleActiveStatusChange(e.target.value as UserActiveStatus)
                  }
                  IconComponent={KeyboardArrowDown}
                  displayEmpty
                  renderValue={(selected) => {
                    const label =
                      selected === "active"
                        ? "Active"
                        : selected === "inactive"
                          ? "Inactive"
                          : "All";
                    return `Status : ${label}`;
                  }}
                  sx={{
                    borderRadius: 2,
                    height: 40,
                    minWidth: { xs: "100%", sm: 170 },
                    backgroundColor: colors.surface,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: colors.label,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.accentPurple,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.accentPurple,
                      borderWidth: 1.5,
                    },
                  }}
                >
                  <MenuItem value="active">Status : Active</MenuItem>
                  <MenuItem value="inactive">Status : Inactive</MenuItem>
                  <MenuItem value="all">Status : All</MenuItem>
                </Select>
              </Box>
            </Box>

            {/* Table / Loading / Empty */}
            {isLoading ? (
              <Box
                sx={{
                  minHeight: "350px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress sx={{ color: colors.accentPurple }} />
              </Box>
            ) : userStore.users.length === 0 ? (
              <Box
                sx={{
                  minHeight: "250px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CustomTypography variant="body1" color={colors.subtitle}>
                  No users found
                </CustomTypography>
              </Box>
            ) : (
              <UserTable
                maxHeight={window.innerHeight - 340}
                users={userStore.users}
                limit={params.limit || userStore.limit}
                page_no={params.page_no}
                total_count={userStore.total_count}
                total_pages={userStore.total_pages}
                sortBy={params.sort_by}
                sortType={params.sort_type}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onDelete={(id) => setDeleteId(id)}
                onActivate={(id) => setActivateId(id)}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
              />
            )}
          </Paper>

          {/* Delete Confirmation Dialog */}
          {deleteId !== null && (
            <DeleteConfirmation
              id={deleteId}
              open={true}
              onDeleteConfirm={() => handleDeleteConfirm(deleteId)}
              onCancel={() => setDeleteId(null)}
              title="Confirm Deletion"
              content="Are you sure you want to delete this user?"
            />
          )}

          {/* Activate Confirmation Dialog */}
          {activateId !== null && (
            <Dialog
              open={true}
              onClose={() => setActivateId(null)}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  p: 1,
                  maxWidth: 420,
                  width: "100%",
                },
              }}
            >
              <DialogTitle>
                <CustomTypography variant="heading6" color={colors.title}>
                  Confirm Activation
                </CustomTypography>
              </DialogTitle>
              <DialogContent>
                <CustomTypography variant="body1" color={colors.subtitle}>
                  Are you sure you want to make this user active?
                </CustomTypography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <AppButton
                  label="Cancel"
                  variant="grayscale"
                  onClick={() => setActivateId(null)}
                />
                <AppButton
                  label="Activate"
                  variant="done"
                  onClick={() => handleActivateConfirm(activateId)}
                />
              </DialogActions>
            </Dialog>
          )}
        </Box>
      )}
    </Grid2>
  );
};

export default observer(UserListing);
